/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-expressions */
import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { FormLabel, Text, Link, Message } from '@resultadosdigitais/tangram-components'
import { ArrowLeft } from '@resultadosdigitais/tangram-react-icons'
import useQuery from '../../../hooks/useQuery'
import Button from 'components/Button'
import PasswordInput from '../../PasswordInput'
import loginService from '../../../login-service'
import { isEmpty } from '../../../validation'
import { backSignup } from '../../../services/redirectSignupLogin'
import { useInstrumentation } from '@resultadosdigitais/front-hub/react'

const InputLabel = styled(FormLabel)`
  font-weight: var(--text-weight-bold);
  color: var(--color-black);
`
const StyledText = styled(Text)`
  text-align: left;
  margin-bottom: var(--size-spacing-02);
  margin-top: 2px;
`

const StyledSubtitle = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: var(--size-spacing-04);
  @media screen and (min-width: 484px) {
    flex-direction: row;
  }
`
const StyledLink = styled(Link)`
  font: var(--text-md-bold);
  margin: 0 2px 0 2px;
  &:hover {
    cursor: pointer;
  }
`
const WrapperLink = styled.div`
  min-width: 72px;
  margin-left: var(--size-spacing-01);
`

const WrapperBackButton = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 72px;

  &:hover {
    cursor: pointer;
  }

  p {
    margin-top: var(--size-spacing-01);
  }
`

const StyledPasswordInput = styled(PasswordInput)`
  margin-top: var(--size-spacing-02);
  margin-bottom: var(--size-spacing-02);
  border-color: ${props => (props.hasError ? 'var(--color-red-50)' : null)};

  &:hover {
    border-color: ${props => (props.hasError ? 'var(--color-red-50)' : null)};
  }
`
const WrapperButton = styled.div`
  margin-top: var(--size-spacing-04);
`

const RecoverPassword = styled(Message)`
  margin: 25px 0 30px 0;

  svg {
    fill: var(--color-green-60);
  }

  ::before {
    background-color: var(--color-green-60);
  }
`

const Error = styled(Message)`
  margin: 25px 0 30px 0;
  max-width: 550px;
  svg {
    fill: var(--color-red-50);
  }

  ::before {
    background-color: var(--color-red-50);
  }
`

const SUBMIT = 'submit'
const BLUR = 'blur'

function FormPassword() {
  const email = useQuery().get('email_signup_account')
  const redirectTo = useQuery().get('redirect_to')
  const backRedirectTo = backSignup(redirectTo)
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)
  const [isSubmiting, setIsSubmiting] = useState(false)
  const [emptyField, setEmptyField] = useState(false)
  const [getRecoverPassword, setGetRecoverPassword] = useState(false)
  const { track } = useInstrumentation()

  const hasErrors = useMemo(() => {
    return error || emptyField
  }, [password])

  useEffect(() => {
    setError(false)
    setEmptyField(false)
  }, [hasErrors])

  const checkErrors = event => {
    if (event === SUBMIT) {
      const isEmptyField = isEmpty(password)
      setError(isEmptyField)
      return isEmptyField
    }

    if (event === BLUR) {
      setEmptyField(isEmpty(password))
    }
  }

  const handlePassword = event => {
    setPassword(event.target.value)
  }

  const hundleSubmit = async event => {
    event.preventDefault()
    try {
      if (checkErrors(SUBMIT)) return
      setIsSubmiting(true)
      const { data } = await loginService.login({ email, password, redirectTo })
      window.location.assign(data.redirectTo)
    } catch (e) {
      console.error(e)
      setError(true)
      setIsSubmiting(false)
      track('Signup: Login Password Error')
    }
  }

  const handleBlur = () => checkErrors(BLUR)

  const handleEditBack = action => {
    track(`Signup: ${action} Button Clicked`)

    window.location.assign(backRedirectTo)
  }

  async function recoverPassword(event, action) {
    event.preventDefault()
    track('Signup: Recover Password Clicked', { action })
    try {
      setIsSubmiting(true)
      await loginService.recoverPassword({ email })
      setGetRecoverPassword(true)
      setIsSubmiting(false)
      setError(false)
    } catch (e) {
      console.error(e)
      setError(true)
      setIsSubmiting(false)
      track('Signup: Recover Password Error', { action })
    }
  }

  const SubtitleWithLink = ({ SubtitleText, LinkText, onClick }) => {
    return (
      <StyledSubtitle>
        <StyledText
          token={Text.tokens.TEXT_SM_REGULAR}
          dangerouslySetInnerHTML={{
            __html: SubtitleText,
          }}
        />
        <WrapperLink>
          (
          <StyledLink token={Text.tokens.TEXT_SM_REGULAR} onClick={onClick} disabled={isSubmiting}>
            {LinkText}
          </StyledLink>
          )
        </WrapperLink>
      </StyledSubtitle>
    )
  }

  return (
    <>
      <form onSubmit={hundleSubmit}>
        <WrapperBackButton onClick={() => handleEditBack('Back')} data-testid="back-edit-button">
          <ArrowLeft style={{ fill: '#636E7C' }} title={t('SignupLogin.Back')} titleId="back-edit" />
          <Text token={Text.tokens.TEXT_SM_BOLD}>{t('SignupLogin.Back')}</Text>
        </WrapperBackButton>
        {getRecoverPassword ? (
          <>
            {error ? (
              <Error title={t('SignupLogin.RecoverPassword.Error')} kind={Message.kinds.warning} dismissible={false} />
            ) : (
              <>
                <StyledText token={Text.tokens.TEXT_XL_BOLD}>{t('SignupLogin.RecoverPassword.Title')}</StyledText>
                <RecoverPassword title={t('SignupLogin.RecoverPassword.Message')} kind={Message.kinds.warning} dismissible={false} />
              </>
            )}
            <SubtitleWithLink
              SubtitleText={t('SignupLogin.RecoverPassword.Confirmation', { email })}
              LinkText={t('SignupLogin.RecoverPassword.Resend')}
              onClick={e => recoverPassword(e, 'send_again')}
            />
          </>
        ) : (
          <>
            <StyledText token={Text.tokens.TEXT_XL_BOLD}>{t('SignupLogin.NewAccount')}</StyledText>
            <SubtitleWithLink
              SubtitleText={t('SignupLogin.ExistingAccount', { email })}
              LinkText={t('SignupLogin.Update')}
              onClick={() => handleEditBack('Edit_Email')}
            />
            {error && <Error title={t('SignupLogin.Error')} kind={Message.kinds.warning} dismissible={false} />}
            <InputLabel htmlFor="password">
              {t('SignupLogin.Password')}
              <StyledPasswordInput
                hasError={error || emptyField}
                id="password"
                name="password"
                autoComplete="new-password"
                data-testid="password-input"
                onChange={handlePassword}
                onBlur={handleBlur}
              />
              {emptyField && (
                <Text data-testid="error-empty-email" style={{ color: 'var(--color-red-50)' }}>
                  {t('SignupLogin.EmptyField')}
                </Text>
              )}
              <StyledLink data-testid="recover-password-button" onClick={e => recoverPassword(e, 'forgot_password')} disabled={isSubmiting}>
                {t('SignupLogin.ForgotPassword')}
              </StyledLink>
            </InputLabel>
            <WrapperButton>
              <Button loading={isSubmiting} type="submit" data-testid="submit-button" disabled={isSubmiting}>
                {t('SignupLogin.CreateAccount')}
              </Button>
            </WrapperButton>
          </>
        )}
      </form>
    </>
  )
}

export default FormPassword
